import { notesService, materialsService } from "@/services";
import { materialText } from "./material-ocr.service";
import { localFiles } from "./document/localFiles";
import type { Material, Topic } from "@/types/models";

/**
 * Die **einzige** Wissensquelle für alles Generierte (Lernpfad, Karteikarten,
 * Probeklausur, Vortragsbewertung).
 *
 * Drei Herkünfte, gleichberechtigt:
 *   1. Mitschrift und Lernzettel (Markdown)
 *   2. der Textinhalt der hochgeladenen PDFs
 *   3. die **eigenen Textblöcke im PDF** – also das, was per Textfeld direkt
 *      ins Arbeitsdokument getippt wurde (PDF-Anmerkungen)
 *
 * Warum eigener Extraktor statt der OCR-Schnittstelle: die meisten PDFs haben
 * bereits eine Textebene. Die auszulesen ist kostenlos, sofort und exakt –
 * OCR wird nur noch für reine Scans gebraucht und läuft nur, wenn ein Schlüssel
 * hinterlegt ist. Vorher lieferten PDFs ohne OCR-Schlüssel schlicht gar nichts,
 * die Aufgaben entstanden dann allein aus den Notizen.
 *
 * Jeder Block behält seine Herkunft (`kind`, `label`, `page`). Damit kann eine
 * Aufgabe auf ihre Fundstelle verweisen, und der Prompt kann verlangen, dass
 * NUR aus diesem Material gearbeitet wird – kein Vorwissen des Modells.
 */

export type SourceKind = "mitschrift" | "lernzettel" | "pdf" | "textblock";

export interface SourceBlock {
  /** Stabiler Anker, z. B. "pdf:abc123:7" – Aufgaben referenzieren ihn. */
  id: string;
  kind: SourceKind;
  /** Anzeigename der Fundstelle, z. B. „Arbeitsblatt Ökologie, S. 3". */
  label: string;
  text: string;
  /** 1-basierte Seite, nur bei PDF-Herkunft. */
  page?: number;
}

export interface TopicSources {
  blocks: SourceBlock[];
  /** Alle Blöcke als ein Prompt-Kontext, gedeckelt. */
  text: string;
  /** Ändert sich, sobald irgendeine Quelle sich ändert. */
  hash: string;
  /** Reine Wortzahl – Grundlage für „reicht das für einen Lernpfad?". */
  words: number;
  /**
   * Titel der PDF-Materialien ohne verwertbaren Text: keine Textebene UND
   * (kein OCR-Schlüssel hinterlegt ODER OCR fand nichts) – reiner Scan.
   * Aufrufer, die gezielt auf ein Material zugreifen wollen (z. B. Sparkles),
   * können das dem Nutzer als Fehler statt als stilles Nichts melden.
   */
  needsOcr: string[];
}

const MAX_PER_BLOCK = 60_000;
const MAX_TOTAL = 90_000;
const CACHE_PREFIX = "pdftext_";

export function hashText(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/* --------------------------- PDF-Textextraktion --------------------------- */

interface PdfExtract {
  /** Seitentext, Index = Seite - 1. */
  pages: string[];
  /** Eigene Textblöcke: getippte Anmerkungen mit ihrer Seite. */
  notes: { page: number; text: string }[];
}

let pdfjsPromise: Promise<typeof import("pdfjs-dist")> | null = null;

/**
 * pdf.js erst laden, wenn wirklich ein PDF gelesen wird – die Bibliothek ist
 * groß, und wer nur mit Mitschriften arbeitet, soll sie nie herunterladen.
 */
async function getPdfjs(): Promise<typeof import("pdfjs-dist")> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const lib = await import("pdfjs-dist");
      const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    })();
  }
  return pdfjsPromise;
}

/** Textebene + getippte Anmerkungen eines PDFs. */
async function extractPdf(bytes: ArrayBuffer): Promise<PdfExtract> {
  const pdfjs = await getPdfjs();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  const pages: string[] = [];
  const notes: { page: number; text: string }[] = [];

  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);

    const content = await page.getTextContent();
    const line = content.items
      .map((it) => ("str" in it ? it.str : ""))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    pages.push(line);

    // FreeText = das, was der Nutzer selbst ins PDF getippt hat. Widget-Felder
    // (Formularfelder) zählen mit, weil die Textwerkzeuge von pdf.js beim
    // Speichern teils solche erzeugen.
    const annots = await page.getAnnotations({ intent: "display" }).catch(() => []);
    for (const a of annots as { subtype?: string; contents?: string; fieldValue?: string }[]) {
      const raw = (a.contents || a.fieldValue || "").trim();
      if (!raw) continue;
      if (a.subtype !== "FreeText" && a.subtype !== "Text" && a.subtype !== "Widget") continue;
      notes.push({ page: p, text: raw });
    }
    page.cleanup();
  }
  await doc.cleanup();
  return { pages, notes };
}

function pdfCacheKey(m: Material): string {
  return `${CACHE_PREFIX}${m.id}_${m.updated}`;
}

function readPdfCache(m: Material): PdfExtract | null {
  try {
    const raw = localStorage.getItem(pdfCacheKey(m));
    return raw ? (JSON.parse(raw) as PdfExtract) : null;
  } catch {
    return null;
  }
}

function writePdfCache(m: Material, data: PdfExtract): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(`${CACHE_PREFIX}${m.id}_`) && k !== pdfCacheKey(m)) localStorage.removeItem(k);
    }
    localStorage.setItem(pdfCacheKey(m), JSON.stringify(data));
  } catch {
    /* Quota – der Cache ist nur Beschleunigung */
  }
}

function isPdf(m: Material): boolean {
  return (m.type ?? "").toLowerCase() === "application/pdf" || /\.pdf$/i.test(m.file);
}

/** PDF-Rohdaten aus dem Cache oder frisch per pdf.js gelesen (siehe `extractPdf`). */
async function loadPdfData(m: Material): Promise<PdfExtract> {
  const cached = readPdfCache(m);
  if (cached) return cached;
  try {
    // Liegt die Datei auf dem Gerät (localFiles.ts), nicht noch einmal laden.
    const local = await localFiles.get(m.id).catch(() => null);
    const buf = local
      ? await local.blob.arrayBuffer()
      : await fetch(materialsService.urlFor(m)).then((r) => r.arrayBuffer());
    const data = await extractPdf(buf);
    writePdfCache(m, data);
    return data;
  } catch {
    return { pages: [], notes: [] };
  }
}

/** Seitentexte zu einem Fließtext zusammengesetzt, mit Seitenzahl-Markierung. */
function pdfBodyText(data: PdfExtract): string {
  return data.pages
    .map((t, i) => (t ? `[S. ${i + 1}] ${t}` : ""))
    .filter(Boolean)
    .join("\n");
}

/**
 * Kompletter lesbarer Text eines Materials – bei PDFs zuerst die echte
 * Textebene (kostenlos, exakt, kein API-Aufruf), erst bei reinen Scans OCR.
 * Andere Dateitypen laufen über den bestehenden `materialText()`-Weg.
 * Grundlage für `materialBlocks()` unten UND für „Text kopieren" in der
 * Materialien-Ansicht (siehe TopicPage.ts).
 */
export async function materialFullText(m: Material): Promise<{ text: string; needsOcr: boolean; viaOcr: boolean }> {
  if (!isPdf(m)) {
    const text = await materialText(m).catch(() => "");
    return { text, needsOcr: !text.trim(), viaOcr: true };
  }
  const body = pdfBodyText(await loadPdfData(m));
  if (body.trim()) return { text: body, needsOcr: false, viaOcr: false };

  // Reiner Scan ohne Textebene: jetzt lohnt sich OCR – aber nur, wenn ein
  // Schlüssel hinterlegt ist (sonst kommt still ein leerer String zurück).
  const ocr = await materialText(m).catch(() => "");
  return { text: ocr, needsOcr: !ocr.trim(), viaOcr: true };
}

/** Blöcke eines einzelnen Materials (gecacht) + ob es ein reiner Scan ohne Text war. */
async function materialBlocks(m: Material): Promise<{ blocks: SourceBlock[]; needsOcr: boolean }> {
  if (!isPdf(m)) {
    const { text, needsOcr } = await materialFullText(m);
    return {
      blocks: text.trim()
        ? [{ id: `mat:${m.id}`, kind: "pdf", label: m.title, text: text.slice(0, MAX_PER_BLOCK) }]
        : [],
      needsOcr,
    };
  }

  const data = await loadPdfData(m);
  const body = pdfBodyText(data);
  const blocks: SourceBlock[] = [];

  let needsOcr = false;
  if (body.trim()) {
    blocks.push({ id: `pdf:${m.id}`, kind: "pdf", label: m.title, text: body.slice(0, MAX_PER_BLOCK) });
  } else {
    // Reiner Scan ohne Textebene: jetzt lohnt sich OCR – aber nur, wenn ein
    // Schlüssel hinterlegt ist (sonst kommt still ein leerer String zurück).
    const ocr = await materialText(m).catch(() => "");
    if (ocr.trim()) {
      blocks.push({ id: `ocr:${m.id}`, kind: "pdf", label: m.title, text: ocr.slice(0, MAX_PER_BLOCK) });
    } else {
      needsOcr = true;
    }
  }

  // Eigene Textblöcke bekommen einen EIGENEN Block je Seite: sie sind das,
  // was der Nutzer selbst für wichtig hielt, und sollen im Prompt sichtbar
  // getrennt vom Fließtext des Dokuments stehen.
  const byPage = new Map<number, string[]>();
  for (const n of data.notes) {
    const list = byPage.get(n.page) ?? [];
    list.push(n.text);
    byPage.set(n.page, list);
  }
  for (const [page, texts] of [...byPage.entries()].sort((a, b) => a[0] - b[0])) {
    blocks.push({
      id: `note:${m.id}:${page}`,
      kind: "textblock",
      label: `${m.title}, S. ${page} (eigene Notiz)`,
      page,
      text: texts.join("\n").slice(0, MAX_PER_BLOCK),
    });
  }

  return { blocks, needsOcr };
}

/* ------------------------------ Öffentlich ------------------------------ */

/**
 * Alle Quellen eines Themas. Reihenfolge ist Absicht: Lernzettel zuerst
 * (didaktisch verdichtet), dann Mitschrift, dann die eigenen Textblöcke aus
 * den PDFs, zuletzt der PDF-Fließtext.
 */
export async function topicSources(topic: Topic | { id: string; title: string }): Promise<TopicSources> {
  const [notes, materials] = await Promise.all([
    notesService.listByTopic(topic.id).catch(() => []),
    materialsService.listByTopic(topic.id).catch(() => [] as Material[]),
  ]);

  const blocks: SourceBlock[] = [];
  const lern = notes.find((n) => n.kind === "lernzettel")?.content?.trim() ?? "";
  const mit = notes.find((n) => n.kind === "mitschrift")?.content?.trim() ?? "";
  if (lern) blocks.push({ id: "note:lernzettel", kind: "lernzettel", label: "Lernzettel", text: lern.slice(0, MAX_PER_BLOCK) });
  if (mit) blocks.push({ id: "note:mitschrift", kind: "mitschrift", label: "Mitschrift", text: mit.slice(0, MAX_PER_BLOCK) });

  const perMaterial = await Promise.all(
    materials.map((m) => materialBlocks(m).catch(() => ({ blocks: [] as SourceBlock[], needsOcr: false })))
  );
  const flat = perMaterial.flatMap((r) => r.blocks);
  blocks.push(...flat.filter((b) => b.kind === "textblock"));
  blocks.push(...flat.filter((b) => b.kind !== "textblock"));
  const needsOcr = materials.filter((_m, i) => perMaterial[i].needsOcr).map((m) => m.title);

  return finish(topic.title, blocks, needsOcr);
}

/** Aus Blöcken den Prompt-Kontext bauen (gedeckelt) und den Hash bilden. */
function finish(title: string, blocks: SourceBlock[], needsOcr: string[]): TopicSources {
  const parts: string[] = [`# Thema: ${title}`];
  let total = 0;
  for (const b of blocks) {
    if (total >= MAX_TOTAL) break;
    const head = `\n## [${b.id}] ${b.label}\n`;
    const chunk = head + b.text;
    parts.push(chunk.slice(0, MAX_TOTAL - total));
    total += chunk.length;
  }
  const text = parts.join("\n");
  const words = blocks.reduce((n, b) => n + b.text.split(/\s+/).filter(Boolean).length, 0);
  return { blocks, text, hash: hashText(text), words, needsOcr };
}

/**
 * Beschriftungsvorschlag für eine einzelne PDF-Seite (für PDF-Verweise im
 * Editor, `[📄 …](pdf:<id>:<seite>)`): die ersten Wörter des Seitentextes
 * statt des Materialnamens, damit ein Verweis erkennen lässt, WAS auf der
 * Seite steht, nicht nur in welcher Datei sie liegt.
 *
 * ponytail: pdf.js liefert pro Seite nur aneinandergereihte Textfragmente
 * ohne echte Zeilenumbrüche – "erste Zeile" gibt es also nicht, genommen
 * werden die ersten ~60 Zeichen bis zur letzten vollen Worttrennung. Bei
 * reinen Scans (kein Textlayer) oder Ladefehlern bleibt es leer, der
 * Aufrufer fällt dann auf den Materialnamen zurück.
 */
export async function pageHeading(pdf: Blob, page: number): Promise<string> {
  const doc = await openPdf(pdf);
  if (page < 1 || page > doc.numPages) return "";
  const content = await (await doc.getPage(page)).getTextContent();
  const text = content.items
    .map((it) => ("str" in it ? it.str : ""))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  const cut = text.slice(0, 60);
  const heading = cut.length < text.length ? cut.replace(/\s+\S*$/, "") : cut;
  return heading.trim();
}

/**
 * Bereits geöffnete Dokumente je Blob: das Arbeits-PDF liegt beim Einfügen
 * eines Verweises ohnehin schon im Speicher. Früher wurde dafür das Material
 * noch einmal übers Netz geladen und der Text ALLER Seiten gelesen – im
 * Schul-WLAN mehrere Sekunden für eine einzige Überschrift.
 */
const openDocs = new WeakMap<Blob, Promise<import("pdfjs-dist").PDFDocumentProxy>>();

function openPdf(pdf: Blob): Promise<import("pdfjs-dist").PDFDocumentProxy> {
  let doc = openDocs.get(pdf);
  if (!doc) {
    doc = (async () => {
      const pdfjs = await getPdfjs();
      return pdfjs.getDocument({ data: new Uint8Array(await pdf.arrayBuffer()) }).promise;
    })();
    openDocs.set(pdf, doc);
    doc.catch(() => openDocs.delete(pdf));
  }
  return doc;
}

/** pdf.js und das Dokument schon im Hintergrund öffnen, damit der erste Verweis sofort kommt. */
export function warmPageHeadings(pdf: Blob): void {
  void openPdf(pdf).catch(() => undefined);
}

/**
 * Kurzer Katalog der Fundstellen – als Anhang an Prompts, damit die KI
 * Referenzen mit gültigen IDs zurückgeben kann statt sie zu erfinden.
 */
export function sourceIndex(src: TopicSources): string {
  return src.blocks.map((b) => `${b.id} = ${b.label}`).join("\n");
}

/**
 * Ausschnitt, der zu einer Frage passt. Bei viel Material darf nicht der
 * ganze Text in jeden Prompt – sonst driften die Aufgaben weg vom Lektionsziel.
 */
export function relevantText(src: TopicSources, query: string, maxChars = 14_000): string {
  if (src.text.length <= maxChars) return src.text;
  const terms = [...new Set(query.toLowerCase().match(/[a-zäöüß]{4,}/g) ?? [])];
  const chunks: { i: number; text: string; score: number }[] = [];
  const size = 1200;
  for (let i = 0; i < src.text.length; i += size) {
    const text = src.text.slice(i, i + size);
    const low = text.toLowerCase();
    const score = terms.reduce((n, t) => n + (low.split(t).length - 1), 0);
    chunks.push({ i, text, score });
  }
  const picked: typeof chunks = [];
  let total = 0;
  for (const c of [...chunks].sort((a, b) => b.score - a.score)) {
    if (total + c.text.length > maxChars) break;
    picked.push(c);
    total += c.text.length;
  }
  if (!picked.length) return src.text.slice(0, maxChars);
  return picked.sort((a, b) => a.i - b.i).map((c) => c.text).join("\n…\n");
}

/** Fachbegriff-Kandidaten aus den Quellen – Kontext-Hilfe für Whisper. */
export function termHint(src: TopicSources): string {
  const words = src.text.match(/\b[A-ZÄÖÜ][a-zäöüß]{5,}\b/g) ?? [];
  const seen = new Map<string, number>();
  for (const w of words) seen.set(w, (seen.get(w) ?? 0) + 1);
  return [...seen.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 60)
    .map(([w]) => w)
    .join(", ");
}
